import type { InteractionEvent, InteractionEventKind } from '@smart-link/shared';
import { getApiBase } from './config';
import { sealBody } from './crypto';
import { sessionId } from './session';
const startTs = typeof window !== 'undefined' ? performance.now() : 0;
const buffer: InteractionEvent[] = [];
const firstSeen = new Set<string>();
let flushedCount = 0;

export function trackEvent(kind: InteractionEventKind, data?: Record<string, unknown>) {
  if (buffer.length >= 200) return;
  buffer.push({
    kind,
    tsOffsetMs: Math.round(performance.now() - startTs),
    data,
  });
}

export function trackFirst(kind: InteractionEventKind, data?: Record<string, unknown>) {
  if (firstSeen.has(kind)) return;
  firstSeen.add(kind);
  trackEvent(kind, data);
}

export function getEvents(): InteractionEvent[] {
  return [...buffer];
}

export function markAllEventsFlushed(): void {
  flushedCount = buffer.length;
}

/**
 * Send any events not yet flushed via sendBeacon (survives page unload).
 * Safe to call repeatedly — only sends events queued since the last flush.
 * Payload is sealed into the AES-GCM envelope (plaintext fallback when Web
 * Crypto is missing); see beacon.ts for the async-vs-unload caveat.
 */
export async function flushEventsBeacon(): Promise<void> {
  const pending = buffer.slice(flushedCount);
  if (pending.length === 0) return;
  flushedCount = buffer.length;
  try {
    const payload = { sessionId, events: pending };
    const url = `${getApiBase()}/api/events`;
    const sealed = await sealBody(payload);
    const blob = sealed
      ? new Blob([sealed], { type: 'application/octet-stream' })
      : new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'content-type': blob.type },
        body: blob,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // ignore
  }
}

if (typeof window !== 'undefined') {
  // page view immediately
  trackEvent('pageView', {
    href: window.location.href,
    referrer: document.referrer || undefined,
  });

  window.addEventListener(
    'mousemove',
    (e) => trackFirst('firstMouseMove', { x: e.clientX, y: e.clientY }),
    { passive: true, once: false },
  );
  window.addEventListener(
    'touchstart',
    (e) => {
      const t = e.touches[0];
      trackFirst('firstTouch', t ? { x: Math.round(t.clientX), y: Math.round(t.clientY) } : {});
    },
    { passive: true },
  );
  window.addEventListener('scroll', () => trackFirst('firstScroll'), { passive: true });
  window.addEventListener('keydown', (e) => trackFirst('firstKey', { key: e.key.slice(0, 4) }), {
    passive: true,
  });
  document.addEventListener('visibilitychange', () =>
    trackEvent('visibilityChange', { state: document.visibilityState }),
  );
  window.addEventListener('devicemotion', () => trackFirst('motionFirstSample'), {
    passive: true,
    once: true,
  });
  window.addEventListener('deviceorientation', () => trackFirst('orientationFirstSample'), {
    passive: true,
    once: true,
  });
}
